// Pepperdine University: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Pepperdine's Common Data Set 2025-2026 (a PDF on Google Drive, linked
//    as "Common Data Set for 2025-2026" from the Office of Institutional
//    Effectiveness page that the SOURCES url names): C1 applicants, admits
//    and enrollees for Fall 2025 (the residency table is blank), C8 test
//    policy, C21/C22 plans, G1/G5 2026-2027 costs. Quirk: this CDS's C8A asks
//    about "students applying for Fall 2025", not fall 2027; the cdsLine says
//    so. C21 gives no early decision counts, noting "ED effective with the
//    fall 2026 applicant pool".
//  - Pepperdine Undergraduate Admission (admission.pepperdine.edu):
//    Application Deadlines, First-Year Applicants, FAQ. The deadlines table
//    carries no year; the Requirements page names "Applicants for Fall 2027"
//    and the Fine Arts page schedules live auditions "for the 2027-2028
//    academic year", so it is this cycle's. Quirk: the FAQ still gives
//    decisions "by January 10" for the early plans and "by April 1" for
//    regular decision, and an ED scholarship of $2,000; the deadlines page
//    gives December 21, February 1 and March 19 and "at least $25,000 in
//    merit aid". The page uses the deadlines page and says the FAQ differs.
//  - Seaver College Fine Arts, Apply to Fine Arts (auditions, portfolios).
//  - College Scorecard, school id 121150 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: CDS G1/G5 2026-2027 first-year figures. The financial aid office's
// 2026-2027 page lists the same tuition, housing and food, wellness and
// campus life fees ($580 + $252 = the CDS's $832), books, transportation and
// personal expenses, plus $66 in loan fees, for $99,258.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Pepperdine Common Data Set 2025-2026',
        url: 'https://www.pepperdine.edu/oie/institutional-research/common-data-set.htm',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Pepperdine Undergraduate Admission, Application Deadlines',
        url: 'https://admission.pepperdine.edu/apply/deadlines/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Pepperdine Undergraduate Admission, First-Year Applicants',
        url: 'https://admission.pepperdine.edu/apply/undergraduate/first-year-applicants.htm',
        read: '2026-10-01',
    },
    faq: {
        label: 'Pepperdine Undergraduate Admission, FAQ',
        url: 'https://admission.pepperdine.edu/apply/undergraduate/faq/',
        read: '2026-10-01',
    },
    fineArts: {
        label: 'Seaver College Fine Arts, Apply to Fine Arts',
        url: 'https://seaver.pepperdine.edu/fine-arts/audition/apply-fine-arts.htm',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?121150-Pepperdine-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'pepperdine-university',
    kbId: 'pepperdine_university',
    name: 'Pepperdine University',
    shortName: 'Pepperdine',
    location: 'Malibu, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Pepperdine community brand page); decoration only.
    branding: {
        primary: '#1E2859',
        secondary: '#E65526',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/pepperdine_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 13445,
        admitted: 9331,
        enrolled: 999,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Pepperdine\'s Common Data Set reports one first-year acceptance rate for Seaver College as a whole, with no rate by division or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action and early decision',
        note: 'The Common Data Set gives no early decision or early action counts for Fall 2025; it notes that early decision took effect "with the fall 2026 applicant pool". So there is no early acceptance rate to show.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Seaver College, Pepperdine\'s undergraduate college, through the Common App. Some programs ask for more:',
        rows: [
            { school: 'Music and Theatre', requirement: 'A supplemental Fine Arts application in the Pepperdine Application Portal and two Fine Arts letters of recommendation. A live audition is required for Music majors and for Theatre majors in the Musical Theatre emphasis, and strongly recommended for other Theatre applicants. Audition days: October 31 (Zoom only) and November 14 for early applicants; January 16 (Zoom only) and January 30 for regular decision. The supplement is due at least 72 hours before your audition and by January 27.', source: 'fineArts' },
            { school: 'Art', requirement: 'No portfolio is required for admission. An optional SlideRoom supplement and a portfolio review (November 5 or January 21) put you in line for additional scholarships.', source: 'fineArts' },
            { school: 'Fine Arts and early decision', requirement: 'Fine Arts applicants are not eligible for Early Decision.', source: 'faq' },
            { school: 'Nursing', requirement: 'Nursing applicants apply through the Common App by January 15, in Regular Decision only; early action and early decision exclude the nursing program.', source: 'faq' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'December 21', binding: false },
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'December 21', binding: true },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'February 1', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 19', binding: false },
        ],
        note: 'The FAFSA is due November 1 for Early Action and Early Decision I, January 15 for Early Decision II and February 15 for Regular Decision. Admitted students enroll by February 1 (Early Decision I), March 1 (Early Decision II) or May 1 (Early Action and Regular Decision). Students admitted in Early Decision are guaranteed at least $25,000 in merit aid. Pepperdine\'s FAQ page still gives older decision dates (January 10 for the early plans, April 1 for Regular Decision); these are the Application Deadlines page\'s.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if admitted, you must enroll at Pepperdine. You can apply early decision to only one college. You can apply early action to other colleges, but you must withdraw those applications if Pepperdine admits you. Early Action is nonbinding. If you are denied in Early Action or Early Decision, you cannot reapply in Regular Decision for the same term.',
        bindingSource: 'faq',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Pepperdine is test-optional: you receive the same consideration for admission whether or not you send SAT, ACT or CLT scores. Scores are required to be considered for the Regents Scholars Program scholarships. If you self-report scores, official scores are required when you enroll. The ACT science section is not required but is recommended for nursing applicants.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission but considered if submitted, with scores due by January 15; its form asks about students applying for fall 2025.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 74370 },
            { item: 'Required fees', amount: 832 },
            { item: 'Food and housing (on campus)', amount: 20490 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Transportation', amount: 1000 },
            { item: 'Other expenses', amount: 1500 },
        ],
        total: 99192,
        note: 'For a first-year student living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 48065,
            byIncome: [
                { income: '$0 to $30,000', amount: 38093 },
                { income: '$30,001 to $48,000', amount: 35990 },
                { income: '$48,001 to $75,000', amount: 38110 },
                { income: '$75,001 to $110,000', amount: 42539 },
                { income: 'Over $110,000', amount: 63600 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Pepperdine\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
